import { ArrowLeft, ExternalLink, Plus, Send, ShieldCheck, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { errorMessage, isApiError } from '@/api/client';
import { useCreateDestination, useDeleteDestination, useDestinations, useDestinationTypes, useTestDestination, useVerifyDestination } from '@/api/hooks';
import type { Destination, DestinationType } from '@/api/types';
import { ConfirmDialog, Dialog } from '@/components/Dialog';
import { Menu } from '@/components/Menu';
import { useToast } from '@/components/toast-context';
import { Badge, Button, Card, EmptyState, ErrorNote, LiveRegion, PageHeader, Skeleton, Spinner, TextField } from '@/components/ui';
import { MOCK } from '@/config';
import { cx } from '@/lib/cx';
import { DESTINATION_ICON } from './icons';

// ------------------------------------------------------------------ verification

function CodeStep({ destination, onDone }: { destination: Destination; onDone: () => void }) {
  const verify = useVerifyDestination();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const formId = `verify-${destination.id}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(code.trim())) {
      setError('Enter the code from the message.');
      return;
    }
    try {
      await verify.mutateAsync({ id: destination.id, code: code.trim() });
      toast({ title: `${destination.label} verified`, description: 'Updates and alerts can go there now.' });
      onDone();
    } catch (err) {
      setError(isApiError(err) ? err.message : errorMessage(err));
    }
  }

  return (
    <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
      <p className="text-sm text-muted-ink">
        We sent a code to <span className="font-medium text-ink">{destination.verification?.sent_to ?? destination.label}</span>. Enter it to confirm.
      </p>
      <TextField
        label="Verification code"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
        error={error}
        className="tabular max-w-40 text-lg tracking-[0.3em]"
        autoFocus
        hint={MOCK ? 'Mock mode: the code is 123456.' : undefined}
      />
      <div>
        <Button type="submit" variant="primary" loading={verify.isPending}>
          Verify
        </Button>
      </div>
    </form>
  );
}

function TelegramStep({ destination, onDone }: { destination: Destination; onDone: () => void }) {
  // Poll until the user presses Start in Telegram and the destination verifies itself.
  const list = useDestinations({ poll: true });
  const current = list.data?.find((d) => d.id === destination.id) ?? destination;
  const toast = useToast();
  const fired = useRef(false);
  useEffect(() => {
    if (current.verified && !fired.current) {
      fired.current = true;
      toast({ title: 'Telegram connected', description: 'Updates and alerts can go there now.' });
      onDone();
    }
  }, [current.verified, onDone, toast]);
  const url = current.verification?.url;
  return (
    <div className="flex flex-col gap-4 text-sm">
      <ol className="list-decimal space-y-1 pl-5 text-muted-ink">
        <li>Open the MyDash bot in Telegram.</li>
        <li>
          Press <span className="font-medium text-ink">Start</span>.
        </li>
        <li>Come back here. This page notices by itself.</li>
      </ol>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex h-10 w-fit items-center gap-2 rounded-control bg-ink px-4 font-medium text-paper dark:bg-yellow dark:text-yellow-ink">
          Open Telegram <ExternalLink className="size-4" aria-hidden />
        </a>
      )}
      <Spinner label="Waiting for you to press Start…" />
    </div>
  );
}

// ------------------------------------------------------------------ add

function AddDestinationDialog({ open, onClose, resume }: { open: boolean; onClose: () => void; resume: Destination | null }) {
  const types = useDestinationTypes();
  const create = useCreateDestination();
  const [type, setType] = useState<DestinationType | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [label, setLabel] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<Destination | null>(null);
  const toast = useToast();
  const pending = created ?? resume;

  const close = () => {
    setType(null);
    setValues({});
    setLabel('');
    setFields({});
    setCreated(null);
    onClose();
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!type) return;
    const missing: Record<string, string> = {};
    for (const f of type.config_fields) if (!(values[f.key] ?? '').trim()) missing[f.key] = `Enter the ${f.label.toLowerCase()}.`;
    setFields(missing);
    if (Object.keys(missing).length) return;
    try {
      const d = await create.mutateAsync({
        type: type.id,
        label: label.trim() || null,
        config: Object.fromEntries(type.config_fields.map((f) => [f.key, (values[f.key] ?? '').trim()])),
      });
      if (d.verified) {
        toast({ title: `${d.label} added`, description: 'We sent a test message to check it works.' });
        close();
      } else setCreated(d);
    } catch (err) {
      if (isApiError(err)) setFields(err.fields);
    }
  }

  const title = pending ? `Verify ${pending.label}` : type ? `Add ${type.name}` : 'Add a destination';
  const formId = 'add-destination';

  return (
    <Dialog
      open={open}
      onClose={close}
      title={title}
      description={!pending && !type ? 'Where should updates and alerts go?' : undefined}
      footer={
        !pending && type ? (
          <>
            <Button onClick={() => setType(null)} icon={<ArrowLeft className="size-4" aria-hidden />}>
              Back
            </Button>
            <Button type="submit" form={formId} variant="primary" loading={create.isPending}>
              {type.id === 'telegram' ? 'Get the link' : 'Add'}
            </Button>
          </>
        ) : undefined
      }
    >
      {pending ? (
        pending.verification?.method === 'link' ? (
          <TelegramStep destination={pending} onDone={close} />
        ) : (
          <CodeStep destination={pending} onDone={close} />
        )
      ) : !type ? (
        <>
          {types.isPending && <Skeleton className="h-48" />}
          {types.isError && <ErrorNote message={errorMessage(types.error)} />}
          <ul className="grid gap-2 sm:grid-cols-2">
            {types.data?.map((t) => {
              const Icon = DESTINATION_ICON[t.id];
              const disabled = !t.configured || t.id === 'push';
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setType(t)}
                    className={cx(
                      'flex w-full items-center gap-3 rounded-card border border-border bg-card p-3 text-left',
                      disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted',
                    )}
                  >
                    <span className="flex size-9 items-center justify-center rounded-[8px] bg-muted">
                      <Icon className="size-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{t.name}</span>
                      {t.id === 'push' ? (
                        <span className="block text-xs text-muted-ink">Set up in the mobile app</span>
                      ) : null}
                    </span>
                    {!t.configured && <Badge>Coming soon</Badge>}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
          {create.isError && !Object.keys(fields).length && <ErrorNote message={errorMessage(create.error)} />}
          {type.help && <p className="text-sm text-muted-ink">{type.help}</p>}
          {type.config_fields.map((f, i) => (
            <TextField
              key={f.key}
              label={f.label}
              placeholder={f.placeholder ?? undefined}
              type={f.secret ? 'password' : f.key === 'address' ? 'email' : f.key === 'phone' ? 'tel' : 'text'}
              autoComplete={f.secret ? 'off' : f.key === 'address' ? 'email' : f.key === 'phone' ? 'tel' : 'off'}
              value={values[f.key] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              error={fields[f.key]}
              autoFocus={i === 0}
              spellCheck={false}
            />
          ))}
          <TextField label="Label" optional value={label} onChange={(e) => setLabel(e.target.value)} placeholder={type.id === 'slack' ? 'Slack #kpis' : type.name} hint="How it shows up in MyDash." />
        </form>
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------------ page

export function DestinationsPage() {
  const destinations = useDestinations();
  const remove = useDeleteDestination();
  const test = useTestDestination();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [resume, setResume] = useState<Destination | null>(null);
  const [deleting, setDeleting] = useState<Destination | null>(null);
  const [live, setLive] = useState('');

  function sendTest(d: Destination) {
    setLive(`Sending a test to ${d.label}…`);
    test.mutate(d.id, {
      onSuccess: () => {
        setLive(`Test sent to ${d.label}.`);
        toast({ title: 'Test sent', description: `Check ${d.label}.` });
      },
      onError: (e) => {
        setLive(`The test to ${d.label} failed.`);
        toast({ tone: 'error', title: `Couldn’t reach ${d.label}`, description: errorMessage(e) });
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Destinations"
        description="Email, text, WhatsApp, Telegram, Discord, Teams, Slack and Notion."
        actions={
          <Button variant="primary" onClick={() => setAdding(true)} icon={<Plus className="size-4" aria-hidden />}>
            Add destination
          </Button>
        }
      />
      <LiveRegion message={live} />
      {destinations.isError && <ErrorNote message={errorMessage(destinations.error)} onRetry={() => void destinations.refetch()} />}
      {destinations.isPending && <Skeleton className="h-28" />}
      {destinations.data?.length === 0 && (
        <EmptyState icon={<Send className="size-5" aria-hidden />} title="No destinations" actions={<Button variant="primary" onClick={() => setAdding(true)}>Add one</Button>}>
          Add where your updates and alerts should go.
        </EmptyState>
      )}
      {destinations.data && destinations.data.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          {destinations.data.map((d) => {
            const Icon = DESTINATION_ICON[d.type];
            return (
              <Card as="li" key={d.id} className="flex min-w-0 items-center gap-3 p-4">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-muted">
                  <Icon className="size-[18px]" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{d.label}</p>
                  <p className="text-xs text-muted-ink capitalize">{d.type === 'sms' ? 'Text message' : d.type}</p>
                  {/* Not in the contract yet: the API reports the last permanent delivery failure. */}
                  {(d as Destination & { last_error?: string | null }).last_error && (
                    <p className="mt-0.5 text-xs text-down">{(d as Destination & { last_error?: string | null }).last_error}</p>
                  )}
                </div>
                {d.verified ? (
                  <Badge tone="good" icon={<ShieldCheck className="size-3" aria-hidden />}>
                    Verified
                  </Badge>
                ) : (
                  <Button size="sm" variant="primary" onClick={() => setResume(d)}>
                    Verify
                  </Button>
                )}
                <Menu
                  label={`More actions for ${d.label}`}
                  items={[
                    { label: 'Send a test', icon: <Send className="size-4" aria-hidden />, disabled: !d.verified, onSelect: () => sendTest(d) },
                    { label: 'Delete', icon: <Trash2 className="size-4" aria-hidden />, danger: true, onSelect: () => setDeleting(d) },
                  ]}
                />
              </Card>
            );
          })}
        </ul>
      )}

      <AddDestinationDialog
        open={adding || !!resume}
        resume={resume}
        onClose={() => {
          setAdding(false);
          setResume(null);
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={`Delete ${deleting?.label ?? ''}?`}
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => {
          const d = deleting;
          if (!d) return;
          remove.mutate(d.id, {
            onSuccess: () => {
              toast({ title: `${d.label} deleted` });
              setDeleting(null);
            },
            onError: (e) => toast({ tone: 'error', title: 'Couldn’t delete it', description: errorMessage(e) }),
          });
        }}
      >
        <p>It is also removed from every update and alert that uses it.</p>
      </ConfirmDialog>
    </div>
  );
}
